<script>
import _ from 'lodash'
import BlueButton from '@/Components/Buttons/BlueButton'
import {formatDate} from '@/global'

export default {
    components: {
        BlueButton,
    },
    props: {
        term: {
            type: String,
            default: ''
        }
    },
    data: () => ({
        searchString: '',
        searchOpen: false,
        results: {},
        loading: false,
        abortController: null
    }),
    computed: {
        resultsTotal() {
            return Object.keys(this.results).reduce((prev, key) => prev + this.results[key].results.length, 0)
        }
    },
    watch: {
        term(newVal, oldVal) {
            if (newVal !== oldVal && newVal.length > 0) {
                this.searchString = newVal
                this.getSearchResults()
            }
        }
    },
    methods: {
        search: _.debounce(function () {
            this.getSearchResults()
        }, 500),
        async getSearchResults() {
            if (this.abortController) {
                this.abortController.abort()
            }

            this.abortController = new AbortController()
            const signal = this.abortController.signal

            this.results = {}
            this.loading = true
            if (this.searchString.length) {
                this.searchOpen = true
                try {
                    const { data } = await axios.get(route('ajax.search.index', this.searchString), { signal })
                    this.results = data.results
                    this.loading = false
                    this.abortController = null
                } catch (error) {
                    if (! axios.isCancel(error)) {
                        throw error
                    }
                }
            } else {
                this.searchOpen = false
                this.loading = false
            }
        },
        randomNumber: function () {
            return Math.floor(Math.random() * 31) + 50
        },
        formatLabel(object, label) {
            if (['Task', 'Activity'].includes(object)) {
                return formatDate(label, this.$page.props.auth.user.dateFormat)
            }

            return label
        }
    }
}
</script>

<template>
    <div class="w-full">
        <div class="relative w-64 transition-all duration-300 ease-in-out xl:w-96">
            <input
                v-model="searchString"
                class="px-2.5 pr-10 w-full h-9.5 text-xs font-bold text-rss-blue-700 bg-rss-light-grey focus:bg-white rounded-xl border-2 border-rss-light-blue-600 focus:border-rss-blue-500 focus:outline-none"
                type="text"
                :placeholder="__('Search')"
                @input="search"
            >
            <div class="flex absolute top-0 right-4 items-center h-9.5 text-sm">
                <i class="fa fa-search" />
            </div>
        </div>

        <div
            v-if="searchOpen"
            class="overflow-auto absolute top-16 left-0 z-20 p-6 px-8 pt-0 space-y-4 w-full bg-white shadow-xl"
            style="max-height: calc(100vh - 4rem);"
        >
            <div class="flex justify-between items-center pb-2 border-b">
                <div
                    v-if="!loading"
                    class="font-bold"
                >
                    {{ __('Found') }} {{ resultsTotal }} {{ __('records matching your search') }} '{{ searchString }}'
                </div>
                <div
                    v-else
                    class="font-bold animate-pulse"
                >
                    {{ __('Searching') }}...
                </div>
                <button
                    class="w-10 h-10 text-xl text-rss-grayblue-500 hover:text-rss-blue-500 hover:bg-rss-light-blue-500 rounded-full focus:outline-none"
                    @click="searchOpen = false"
                >
                    <i class="fas fa-times" />
                </button>
            </div>
            <div
                v-if="loading"
                class="flex justify-between w-full"
            >
                <div
                    v-for="columnIndex in 4"

                    :key="columnIndex"
                    class="w-full sm:w-1/2 md:w-1/4"
                >
                    <div
                        v-for="rowIndex in 10"
                        :key="rowIndex"
                        class="flex items-center h-6"
                    >
                        <div
                            class="h-3.5 bg-gray-300  rounded animate-pulse"
                            :style="'width: ' + randomNumber() + '%;'"
                        />
                    </div>
                </div>
            </div>
            <div
                v-else
                class="flex flex-wrap justify-between space-x-6 w-full"
            >
                <div
                    v-for="(data, object) in results"
                    :key="object"
                    class="flex-1 w-1/2 md:w-1/4"
                >
                    <div class="flex justify-between items-center py-2">
                        <a
                            :href="$route('record.index', [data.object.ObjectNames.toLowerCase(), {'term': searchString }])"
                            class="text-lg hover:underline"
                        >
                            {{ data.object.ObjectNames }}
                        </a>
                        <a
                            :href="$route('record.create', data.object.ObjectName.toLowerCase())"
                            class="ml-4"
                        >
                            <BlueButton
                                button-classes="text-2xs flex items-center"
                                style="padding: 0.125rem 0.75rem"
                            >
                                <i class="mr-2 text-base fa fa-plus" />
                                <span>{{ __('Add') }} </span>
                            </BlueButton>
                        </a>
                    </div>
                    <div class="mt-2">
                        <div v-if="data.results.length > 0">
                            <div
                                v-for="(item, index) in data.results"
                                :key="item.recordId"
                                class="text-sm"
                            >
                                <template v-if="index < 10">
                                    <a

                                        :href="$route('record.show', [data.object.ObjectName.toLowerCase(), item.recordId])"
                                        class="font-bold hover:underline"
                                    >
                                        {{ formatLabel(data.object.ObjectName, item.recordLabel) }}
                                    </a>
                                    <template v-if="item.parentLabel">
                                        -
                                        <a
                                            v-if="item.parentLabel"
                                            :href="$route('record.show', [data.object.ParentObject.toLowerCase(), item.parentId])"
                                        >
                                            ({{ item.parentLabel }})
                                        </a>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="mt-2 text-xs text-center">
                        <span v-if="data.results.length && data.results.length > 10">
                            {{ __('Showing') }} 10 {{ __('of') }} {{ data.results.length }}
                        </span>
                        <span v-else-if="data.results.length < 1">
                            {{ __('No records found') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
